import { RootState } from 'base/reducer/reducer';
import { convertToMapItems } from 'components/Form/controls/MapPicker/MapPicker/helpers';
import { DateColumnModel, FormattedReferenceColumnModel, GenericColumnModel, MapOptionColumnModel, OptionColumnModel, StringColumnModel } from 'components/Table';
import { useGetStatusStyleCallback, useOrganizationalUnitCallback } from 'features/TableColumns/persistedHooks';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ProjectResponse } from 'services/tenantManagementService';
import { EntityPrefixEnum } from 'utils/commonHelper';
import { propertyOf } from 'utils/propertyOf';

export const defaultVisibleColumns = [
	propertyOf<ProjectResponse>('id'),
	propertyOf<ProjectResponse>('name'),
	propertyOf<ProjectResponse>('pmId'),
	propertyOf<ProjectResponse>('statusId'),
	propertyOf<ProjectResponse>('typeId'),
	propertyOf<ProjectResponse>('categoryId'),
	propertyOf<ProjectResponse>('organizationalUnitId'),
]

export const defaultDefaultProjectVisibleColumns = [
	...defaultVisibleColumns,
	'defaultProject'
]

export const useTableColumnsMemo = (defaultProjectId?: number): GenericColumnModel[] => {
	const persistedUser = useSelector((state: RootState) => state.persistedUser);
	const persistedProjectStatus = useSelector((state: RootState) => state.persistedProjectStatus);
	const persistedProjectCategory = useSelector((state: RootState) => state.persistedProjectCategory);
	const persistedProjectType = useSelector((state: RootState) => state.persistedProjectType);
	const persistedOrganizationalUnit = useSelector((state: RootState) => state.persistedOrganizationalUnit);
	const persistedBusinessPartner = useSelector((state: RootState) => state.persistedBusinessPartner);
	const persistedSupportOrganization = useSelector((state: RootState) => state.persistedSupportOrganization);

	const getOrganizationalUnit = useOrganizationalUnitCallback();
	const getStatusStyle = useGetStatusStyleCallback(persistedProjectStatus);

	const organizationalUnits = useMemo(
		() => convertToMapItems(persistedOrganizationalUnit.items || [], [], undefined),
		[persistedOrganizationalUnit]
	)

	return useMemo(
		() => [
			new FormattedReferenceColumnModel({
				id: propertyOf<ProjectResponse>('id'),
				title: 'i18n.p.myProjects.column.projectId',
				entityPrefix: EntityPrefixEnum.PROJECT,
				getStyle: (value: any) => value === defaultProjectId ? {
						color: 'var(--brand-color)',
						fontWeight: 'bold'
					} : {}
			}),
			new StringColumnModel({
				id: propertyOf<ProjectResponse>('name'),
				title: 'i18n.p.myProjects.column.name'
			}),
			new OptionColumnModel({
				id: propertyOf<ProjectResponse>('pmId'),
				title: 'i18n.p.myProjects.column.manager',
				items: persistedUser.items,
				getItemId: (option) => option.id,
				getItemText: (option) => `${option.firstName} ${option.lastName}`
			}),
			new OptionColumnModel({
				id: propertyOf<ProjectResponse>('partnerId'),
				title: 'i18n.p.myProjects.column.businessPartner',
				items: persistedBusinessPartner.items,
				getItemId: (option) => option.id,
				getItemText: (option) => option.name
			}),
			new OptionColumnModel({
				id: propertyOf<ProjectResponse>('statusId'),
				title: 'i18n.p.myProjects.column.status',
				getStyle: (value: any) => getStatusStyle(value),
				items: persistedProjectStatus.items,
				getItemId: (option) => option.id,
				getItemText: (option) => option.name
			}),
			new OptionColumnModel({
				id: propertyOf<ProjectResponse>('typeId'),
				title: 'i18n.p.myProjects.column.type',
				items: persistedProjectType.items,
				getItemId: (option) => option.id,
				getItemText: (option) => option.name
			}),
			new OptionColumnModel({
				id: propertyOf<ProjectResponse>('categoryId'),
				title: 'i18n.p.myProjects.column.category',
				items: persistedProjectCategory.items,
				getItemId: (option) => option.id,
				getItemText: (option) => option.name
			}),
			new OptionColumnModel({
				id: propertyOf<ProjectResponse>('supportOrganizationId'),
				title: 'i18n.p.myProjects.column.supportOrganization',
				items: persistedSupportOrganization.items,
				getItemId: (option) => option.id,
				getItemText: (option) => option.name
			}),
			new StringColumnModel({
				id: propertyOf<ProjectResponse>('description'),
				title: 'i18n.p.myProjects.column.description'
			}),
			new MapOptionColumnModel({
				id: propertyOf<ProjectResponse>('organizationalUnitId'),
				title: 'i18n.p.myProjects.column.orgUnit',
				items: organizationalUnits,
				getItemId: (option) => option.id,
				getItemText: (option) => getOrganizationalUnit(option.id, organizationalUnits)
			}),
			new DateColumnModel({
				id: propertyOf<ProjectResponse>('from'),
				title: 'i18n.p.myProjects.column.start'
			}),
			new DateColumnModel({
				id: propertyOf<ProjectResponse>('to'),
				title: 'i18n.p.myProjects.column.end'
			})
		],
		[persistedUser.items, persistedBusinessPartner.items, persistedProjectStatus.items, persistedProjectType.items, persistedProjectCategory.items, persistedSupportOrganization.items, organizationalUnits, defaultProjectId, getStatusStyle, getOrganizationalUnit]
	)
}
